// Angular modules
import { Component, ViewChildren, EventEmitter } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Inject } from '@angular/core';
import { OnInit, OnDestroy, QueryList } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

// External modules
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { config, Observable, Subject, Subscription, timer } from 'rxjs';

// Services
import { ApiService } from '../../shared/services/api.service';
import { NotificationService } from '../../shared/components/blocks/notification/notification.service';
import { AuthService } from 'src/app/shared/services/auth.service';

// Components
import { MessageDialogComponent } from '../../shared/components/dialogs/message-dialog/message-dialog.component';
import { YesNoDialogComponent } from 'src/app/shared/components/dialogs/yesno-dialog/yesno-dialog.component';
import { ExportMessagesDialogComponent } from './export-messages-dialog/export-messages-dialog.component';
import { ExportAuditDialogComponent } from './export-audit-dialog/export-audit-dialog.component';

// Models
import { Message } from '../../shared/models/message.model';
import { DeviceConfiguration, StorageConfiguration } from '../../shared/models/configuration.model';
import { Device } from '../../shared/models/device.model';
import { AuditTrail } from 'src/app/shared/models/audittrail.model';
import { Product } from 'src/app/shared/models/product.model';


@Component({
  selector: 'app-device-detail',
  templateUrl: './device-detail.component.html',
  styleUrls: ['./device-detail.component.scss']
})
export class DeviceDetailPopupComponent implements OnInit, OnDestroy {

  public selectedTabIndex = 0;
  public updating: boolean = false;
  public loading: boolean = false;
  public sending: boolean = false;

  public selectedDeviceInput: string = null;
  public currentDevice: Device;
  public sendingStatusColor: String = null;
  public currentDeviceConfiguration: DeviceConfiguration;
  public selectedStorageConfiguration: StorageConfiguration = null;
  public selectedProduct: Product = null;

  public allOperations: any[];
  public allProducts: Product[];

  public storageForm: FormGroup;
  public cyclicalCallForm: FormGroup;
  public manualCommandForm: FormGroup;

  public messagesDataSource: MatTableDataSource<Message> = new MatTableDataSource();

  public auditTrailDataSource: MatTableDataSource<AuditTrail> = new MatTableDataSource();

  private requestMessagesReload: EventEmitter<Object> = null;
  private configurationLoaded: EventEmitter<Boolean> = null;

  private checkConfigurationUpdateSubscription: Subscription = null;


  @ViewChildren(MatPaginator) paginators = new QueryList<MatPaginator>();

  constructor
    (
      public authService: AuthService,
      private dialogRef: MatDialogRef<DeviceDetailPopupComponent>,
      private dialog: MatDialog,
      private apiService: ApiService,
      private translationService: TranslateService,
      private notificationService: NotificationService,
      private formBuilder: FormBuilder,
      @Inject(MAT_DIALOG_DATA) public data: { device: Device }
    ) {
    this.currentDevice = this.data.device;
    console.log('last device config version: ', this.data.device.configurationVersion);

    this.requestMessagesReload = new EventEmitter<Object>();
    this.requestMessagesReload.subscribe(() => {
      this.loadMessages();
      this.loadAudit();
    });

    this.configurationLoaded = new EventEmitter<Boolean>();
    this.configurationLoaded.subscribe((resetStorage) => {
      if (resetStorage) {
        this.selectedDeviceInput = 'E1';
      }
      this.selectStorage(this.selectedDeviceInput);

      this.parseCurrentSchedulingSetting(this.currentDeviceConfiguration?.configuration?.callSchedule);
    });
  }

  public ngOnInit(): void {

    this.buildStorageForm();
    this.buildCyclicalForm();
    this.buildManualCommandForm();

    this.loadProducts().subscribe(_ => {
      this.loadDeviceConfiguration();
    });
  }

  public ngOnDestroy(): void {
    if (this.checkConfigurationUpdateSubscription !== null)
      this.checkConfigurationUpdateSubscription.unsubscribe();
  }


  // -------------------------------------------------------------------------------
  // ---- NOTE Actions -------------------------------------------------------------
  // -------------------------------------------------------------------------------

  public onTabChange(tab): void {
    if (tab.index === 4) {
      this.loadMessages();
    } else if (tab.index === 5) {
      this.loadAudit();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onChangeDeviceInput(val): void {
    if (this.storageForm.dirty) {
      const dialogRef = this.dialog.open(YesNoDialogComponent, {
        width: null,
        height: null,
        data: {
          Content: this.translationService.instant('DEVICE_DETAIL.TAB_STORAGE.CONFIRM_STORAGE_SELECTION')
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result !== true) {
          this.selectedDeviceInput = this.selectedStorageConfiguration.inputName;
          return;
        }

        this.selectStorage(val);
      });
    } else {
      this.selectStorage(val);
    }
  }

  public onChangeProduct(evt): void {
    this.selectedProduct = this.allProducts.find((p) => p.id === evt.value);
    if (this.selectedProduct) {
      this.selectedStorageConfiguration.unit = this.selectedProduct.unit;

      // If the capacity field is set up, we have to recompute the technical capacity
      const capacityVal = this.storageForm.get('capacity').value;
      if (capacityVal !== undefined && capacityVal !== null) {
        const capacity = parseFloat(capacityVal);
        this.storageForm.get('geometricalCapacity').setValue(Math.round(capacity * this.selectedProduct.conversionFactor));
      }
    } else {
      this.selectedStorageConfiguration.unit = null;
    }
  }

  public onCapacityChange(evt): void {
    if (this.selectedProduct) {
      const capacity = parseFloat(evt.target.value);
      this.storageForm.get('geometricalCapacity').setValue(Math.round(capacity * this.selectedProduct.conversionFactor));
    }
  }

  public onGeometricalCapacityChange(evt): void {
    if (this.selectedProduct) {
      const geometricalCapacity = parseFloat(evt.target.value);
      this.storageForm.get('capacity').setValue(Math.round(geometricalCapacity / this.selectedProduct.conversionFactor));
    }
  }

  public onClickUpdateDeviceConfiguration(): void {
    this.selectedStorageConfiguration.productId = this.storageForm.get('product').value;
    if (this.selectedStorageConfiguration.active) {
      this.selectedStorageConfiguration.capacity = this.storageForm.get('capacity').value;
      this.selectedStorageConfiguration.geometricalCapacity = this.storageForm.get('geometricalCapacity').value;
      this.selectedStorageConfiguration.technicalCapacity = this.storageForm.get('technicalCapacity').value;
      this.selectedStorageConfiguration.minTechnicalCapacity = this.storageForm.get('minTechnicalCapacity').value;
      this.selectedStorageConfiguration.disableConvert4_20 = this.storageForm.get('disableConvert4_20').value;
      this.selectedStorageConfiguration.transmitterType = this.storageForm.get('transmitterType').value;
      this.selectedStorageConfiguration.lowThreshold = this.storageForm.get('lowThreshold').value;
      this.selectedStorageConfiguration.lowLowThreshold = this.storageForm.get('lowLowThreshold').value;
      this.selectedStorageConfiguration.createAlarmOnEiu = this.storageForm.get('createAlarmOnEiu').value;
    } else {
      this.selectedStorageConfiguration.capacity = null;
      this.selectedStorageConfiguration.geometricalCapacity = null;
      this.selectedStorageConfiguration.technicalCapacity = null;
      this.selectedStorageConfiguration.minTechnicalCapacity = null;
      this.selectedStorageConfiguration.disableConvert4_20 = false;
      this.selectedStorageConfiguration.transmitterType = null;
      this.selectedStorageConfiguration.lowThreshold = null;
      this.selectedStorageConfiguration.lowLowThreshold = null;
      this.selectedStorageConfiguration.createAlarmOnEiu = false;
    }

    this.updating = true;

    this.apiService.updateConfiguration(this.currentDeviceConfiguration.id, this.currentDeviceConfiguration.configurationVersion, this.selectedStorageConfiguration).subscribe({
      next: res => {
        this.selectedStorageConfiguration.thresholdsEdited = res;

        this.requestMessagesReload.emit(null);
        this.notificationService.displaySuccess(this.translationService.instant('DEVICE_DETAIL.TAB_STORAGE.SUCCESS'));

        this.storageForm.markAsPristine();
      },
      error: () => {
        this.updating = false;
        this.notificationService.displayError(this.translationService.instant('DEVICE_DETAIL.TAB_STORAGE.ERROR'));
      },
      complete: () => {
        this.updating = false;
      }
    });
  }

  public onClickSendCommand(): void {
    this.sendCommand(this.manualCommandForm.controls['command'].value['command'], this.manualCommandForm.controls['commandParameter'].value).subscribe((res) => {
      // Clear fields
      if (res) {
        this.manualCommandForm.reset();
      }
    });
  }

  public onClickUpdateSchedulingCommand(): void {
    const callPeriodType = this.cyclicalCallForm.get('callPeriodType').value;
    let scheduling = `${callPeriodType} `;
    if (callPeriodType === 'w') {
      this.cyclicalCallForm.get('callWeekDays').value.forEach(element => {
        scheduling += element;
      });
      scheduling += ' ';
    } else if (callPeriodType === 'm') {
      scheduling += this.cyclicalCallForm.get('callDay').value;
      scheduling += ' ';
    }
    scheduling += this.cyclicalCallForm.get('callTime').value;

    this.updating = true;

    this.apiService.updateScheduling(this.currentDevice.id, this.currentDeviceConfiguration.configurationVersion, scheduling).subscribe({
      next: res => {
        this.requestMessagesReload.emit(null);

        this.currentDeviceConfiguration.configuration.callScheduleEdited = true;

        this.cyclicalCallForm.markAsPristine();
        if (res) {
          this.notificationService.displaySuccess(this.translationService.instant('DEVICE_DETAIL.TAB_CYCLICAL_CALL.SUCCESS'));
        }
      },
      error: () => {
        this.updating = false;
        this.notificationService.displayError(this.translationService.instant('DEVICE_DETAIL.TAB_CYCLICAL_CALL.ERROR'));
      },
      complete: () => {
        this.updating = false;
      }
    });
  }

  public openExportMessagesDialog(): void {
    this.dialog.open(ExportMessagesDialogComponent,
      {
        disableClose: false,
        width: '500px',
        data: { deviceId: this.currentDevice.id }
      }
    );
  }

  public openExportAuditDialog(): void {
    this.dialog.open(ExportAuditDialogComponent,
      {
        disableClose: false,
        width: '500px',
        data: { deviceId: this.currentDevice.id }
      }
    );
  }

  public loadMessages(): void {
    const startDate = moment().subtract(14, 'day').startOf('day').toDate();
    const endDate = moment().endOf('day').toDate();

    this.loading = true;

    this.apiService.getMessages(this.currentDevice.id, startDate, endDate).subscribe({
      next: (messages) => {
        messages.forEach(item => {
          if (item.origin === 0) {
            item.title = item.content.substring(0, item.content.indexOf('\n'));
          }
          item.content = item.content.replace(/\r\n/g, '<br/>',);
        });
        this.messagesDataSource.data = messages;
        this.messagesDataSource.paginator = this.paginators.toArray()[0];
      },
      error: () => {
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  public loadAudit(): void {
    const startDate = moment().subtract(31, 'day').startOf('day').toDate();
    const endDate = moment().endOf('day').toDate();

    this.loading = true;

    this.apiService.getAuditTail(this.currentDevice.id, startDate, endDate).subscribe({
      next: (auditItems) => {
        auditItems.forEach(item => {
          item.content = item.content.replace(/\r\n/g, '<br/>',);
        });
        this.auditTrailDataSource.data = auditItems;
        this.auditTrailDataSource.paginator = this.paginators.toArray()[1];
      },
      error: () => {
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private buildStorageForm(): void {
    this.storageForm = this.formBuilder.group({
      product: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required]),
      capacity: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.min(0)]),
      geometricalCapacity: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required, Validators.min(0)]),
      technicalCapacity: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required, Validators.min(0)]),
      minTechnicalCapacity: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.min(0)]),
      disableConvert4_20: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, []),
      transmitterType: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, []),
      lowThreshold: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required, Validators.min(0)]),
      lowLowThreshold: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required, Validators.min(0)]),
      createAlarmOnEiu: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [])
    }, {
      validators: [thresholdValidator()],
      updateOn: 'change'
    });
  }

  private buildCyclicalForm(): void {
    this.cyclicalCallForm = this.formBuilder.group({
      callPeriodType: new FormControl({
        value: 'h',
        disabled: !this.authService.isSuperAdminUser
      }, [Validators.required]),
      callWeekDays: new FormControl({
        value: [],
        disabled: !this.authService.isSuperAdminUser
      }, []),
      callDay: new FormControl({
        value: 1,
        disabled: !this.authService.isSuperAdminUser
      }, []),
      callTime: new FormControl({
        value: '',
        disabled: !this.authService.isSuperAdminUser
      }, [Validators.required])
    });

    this.cyclicalCallForm.get('callPeriodType').valueChanges.subscribe(val => {
      if (!val || val === null) return;
      if (val === 'w') {
        this.cyclicalCallForm.controls['callDay'].clearValidators();
        this.cyclicalCallForm.controls['callWeekDays'].setValidators([Validators.required]);
        this.cyclicalCallForm.controls['callWeekDays'].updateValueAndValidity();
        this.cyclicalCallForm.controls['callDay'].updateValueAndValidity();
      } else if (val === 'm') {
        this.cyclicalCallForm.controls['callWeekDays'].clearValidators();
        this.cyclicalCallForm.controls['callDay'].setValidators([Validators.required, Validators.min(0), Validators.max(31)]);
        this.cyclicalCallForm.controls['callDay'].updateValueAndValidity();
        this.cyclicalCallForm.controls['callWeekDays'].updateValueAndValidity();
      }
    });
  }

  private parseCurrentSchedulingSetting(val: string): void {
    if (!val || val === null) {
      this.cyclicalCallForm.reset();
    } else {
      const parts = val.split(' ');
      if (parts.length > 0) {
        this.cyclicalCallForm.get('callPeriodType').setValue(parts[0]);
      }
      if (parts.length > 1) {
        if (parts[0] === 'w') {
          this.cyclicalCallForm.get('callWeekDays').setValue(parts[1].split('').map((v) => Number.parseInt(v)));
        }
        if (parts[0] === 'm') {
          this.cyclicalCallForm.get('callDay').setValue(parts[1]);
        }

        if ((parts[0] === 'h' || parts[0] === 't') && parts.length > 1) {
          this.cyclicalCallForm.get('callTime').setValue(parts[1]);
        }
        if ((parts[0] === 'w' || parts[0] === 'm') && parts.length > 2) {
          this.cyclicalCallForm.get('callTime').setValue(parts[2]);
        }
      }

      this.cyclicalCallForm.markAsPristine();
    }
  }

  private buildManualCommandForm(): void {
    var operations = new Array<any>();
    if (this.authService.isSuperAdminUser) {
      operations.push({
        'command': 'Analog',
        'label': 'Analog',
        'allowsParameter': false
      });
      operations.push({
        'command': 'AnzMax',
        'label': 'AnzMax',
        'allowsParameter': true,
        'parameterMandatory': true
      });
      for (let i = 1; i <= 4; i++) {
        operations.push({
          'command': `Analog G${i}`,
          'label': `Analog G${i}`,
          'allowsParameter': true,
          'parameterMandatory': true
        });
      }
      operations.push({
        'command': 'Analog GT',
        'label': 'Analog GT',
        'allowsParameter': true,
        'parameterMandatory': true
      });
      operations.push({
        'command': 'Konfig',
        'label': 'Konfig',
        'allowsParameter': false
      });
      for (let i = 1; i <= 6; i++) {
        operations.push({
          'command': `Nummer${i}`,
          'label': `Nummer ${i}`,
          'allowsParameter': true,
          'parameterMandatory': true
        });
      }
      operations.push({
        'command': 'Status',
        'label': 'Status',
        'allowsParameter': false
      });
    }

    operations.push({
      'command': 'Reset',
      'label': 'Reset',
      'allowsParameter': false
    });

    if (this.authService.isSuperAdminUser) {
      operations.push({
        'command': 'RMode',
        'label': 'RMode',
        'allowsParameter': true,
        'parameterMandatory': true
      });
      operations.push({
        'command': 'Routine',
        'label': 'Routine',
        'allowsParameter': true,
        'parameterMandatory': true
      });
    }

    operations.push({
      'command': 'synchronize_clock',
      'label': 'Server Zeit',
      'allowsParameter': false
    });

    if (this.authService.isSuperAdminUser) {
      operations.push({
        'command': 'Zeit',
        'label': 'Zeit',
        'allowsParameter': true,
        'parameterMandatory': true
      });
      operations.push({
        'command': 'SMSC',
        'label': 'SMSC',
        'allowsParameter': true,
        'parameterMandatory': false
      });
      operations.push({
        'command': 'freetext|',
        'label': 'Freier Text',
        'allowsParameter': true,
        'parameterMandatory': true
      });
    }

    this.allOperations = operations;

    this.manualCommandForm = this.formBuilder.group({
      command: new FormControl({
        value: null,
        disabled: !this.authService.isAdminUser && !this.authService.isSuperAdminUser
      }, [Validators.required]),
      commandParameter: new FormControl({
        value: null,
        disabled: false
      }, [])
    });

    this.manualCommandForm.get('command').valueChanges.subscribe(val => {
      if (!val || val === null) return;
      this.manualCommandForm.controls['commandParameter'].setValue(null);
      if (val['allowsParameter'] && val['parameterMandatory']) {
        this.manualCommandForm.controls['commandParameter'].setValidators([Validators.required]);
      } else {
        this.manualCommandForm.controls['commandParameter'].clearValidators();
      }
      this.manualCommandForm.controls['commandParameter'].updateValueAndValidity();
    });
  }

  private selectStorage(inputName: string): void {
    if (inputName !== undefined && inputName !== null) {
      this.selectedStorageConfiguration = this.currentDeviceConfiguration.storages[inputName];
    } else {
      return;
    }

    this.storageForm.get('product').setValue(this.selectedStorageConfiguration.productId);
    if (this.selectedStorageConfiguration.productId !== null) {
      this.selectedProduct = this.allProducts.find(p => p.id === this.selectedStorageConfiguration.productId);
    }

    if (this.selectedStorageConfiguration.capacity !== undefined && this.selectedStorageConfiguration.capacity !== null) {
      this.storageForm.get('capacity').setValue(Math.round(this.selectedStorageConfiguration.capacity));
    } else {
      this.storageForm.get('capacity').setValue(null);
    }
    if (this.selectedStorageConfiguration.geometricalCapacity !== undefined && this.selectedStorageConfiguration.geometricalCapacity !== null) {
      this.storageForm.get('geometricalCapacity').setValue(Math.round(this.selectedStorageConfiguration.geometricalCapacity));
    } else {
      this.storageForm.get('geometricalCapacity').setValue(null);
    }
    if (this.selectedStorageConfiguration.technicalCapacity !== undefined && this.selectedStorageConfiguration.technicalCapacity !== null) {
      this.storageForm.get('technicalCapacity').setValue(Math.round(this.selectedStorageConfiguration.technicalCapacity));
    } else {
      this.storageForm.get('technicalCapacity').setValue(null);
    }
    if (this.selectedStorageConfiguration.minTechnicalCapacity !== undefined && this.selectedStorageConfiguration.minTechnicalCapacity !== null) {
      this.storageForm.get('minTechnicalCapacity').setValue(Math.round(this.selectedStorageConfiguration.minTechnicalCapacity));
    } else {
      this.storageForm.get('minTechnicalCapacity').setValue(null);
    }
    this.storageForm.get('disableConvert4_20').setValue(this.selectedStorageConfiguration.disableConvert4_20);
    this.storageForm.get('transmitterType').setValue(this.selectedStorageConfiguration.transmitterType);
    if (this.selectedStorageConfiguration.lowThreshold !== undefined && this.selectedStorageConfiguration.lowThreshold !== null) {
      this.storageForm.get('lowThreshold').setValue(Math.round(this.selectedStorageConfiguration.lowThreshold));
    } else {
      this.storageForm.get('lowThreshold').setValue(null);
    }
    if (this.selectedStorageConfiguration.lowLowThreshold !== undefined && this.selectedStorageConfiguration.lowLowThreshold !== null) {
      this.storageForm.get('lowLowThreshold').setValue(Math.round(this.selectedStorageConfiguration.lowLowThreshold));
    } else {
      this.storageForm.get('lowLowThreshold').setValue(null);
    }
    if (this.selectedStorageConfiguration.createAlarmOnEiu !== undefined && this.selectedStorageConfiguration.createAlarmOnEiu !== null) {
      this.storageForm.get('createAlarmOnEiu').setValue(this.selectedStorageConfiguration.createAlarmOnEiu);
    } else {
      this.storageForm.get('createAlarmOnEiu').setValue(null);
    }
    this.storageForm.markAsPristine();
  }

  private sendCommand(command: string, commandParameter: string): Subject<boolean> {
    this.sending = true;

    let cmd = command;
    let cmdParam = commandParameter;
    if (cmd === 'freetext|') {
      cmd = `freetext|${cmdParam}`;
      cmdParam = null;
    } else if (cmd === 'synchronize_clock') {
      cmdParam = null;
    } else if (cmd === 'SMSC' && !cmdParam) {
      cmd = 'freetext|SMSC: %SMSC_NUMBER%';
      cmdParam = null;
    } else {
      cmd = `freetext|${cmd}`;
    }

    const subject = new Subject<boolean>();

    this.apiService.sendCommand(this.currentDevice.id, cmd, cmdParam).subscribe(
      {
        next: res => {
          if (res) {
            this.requestMessagesReload.emit(null);
            this.notificationService.displaySuccess(this.translationService.instant('DEVICE_DETAIL.TAB_MANUAL_SMS.SUCCESS'));
          } else {
            this.notificationService.displaySuccess(this.translationService.instant('DEVICE_DETAIL.TAB_MANUAL_SMS.ERROR'));
          }
        },
        error: () => {
          this.sending = false;
          this.notificationService.displayError(this.translationService.instant('DEVICE_DETAIL.TAB_MANUAL_SMS.ERROR'));

          subject.next(false);
        },
        complete: () => {
          this.sending = false;
          subject.next(true);
        }
      });

    return subject;
  }

  private loadDeviceConfiguration(): void {
    this.loading = true;

    this.apiService.getConfiguration(this.currentDevice.id).subscribe({
      next: (configuration) => {
        if (configuration !== null) {
          this.currentDeviceConfiguration = configuration;

          this.configurationLoaded.emit(true);

          this.checkConfigurationUpdateSubscription = timer(0, 10000).subscribe(() => {
            this.checkDeviceConfigurationUpdate(this.currentDevice.internalId);
          });
        } else {
          // No configuration available for the device
          this.dialogRef.close();
          this.dialog.open(MessageDialogComponent, {
            width: null,
            height: null,
            data: {
              Content: this.translationService.instant('DEVICE_DETAIL.NO_CONFIGURATION_AVAILABLE')
            }
          });
        }
      },
      error: () => {
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private checkDeviceConfigurationUpdate(internalId: string): void {
    this.apiService.comstatus([internalId]).subscribe(statuses => {
      if (statuses !== null && statuses.length === 1) {
        const status = statuses[0];

        this.sendingStatusColor = Message.getColorByStatus(status.status);

        if (status.configurationVersion !== null && (this.data.device.configurationVersion === null || status.configurationVersion > this.data.device.configurationVersion)) {
          console.log('the device configuration has been updated');
          // The configuration has been updated, we reload the configuration
          this.apiService.getConfiguration(this.currentDevice.id).subscribe((configuration) => {
            this.currentDeviceConfiguration = configuration;
            this.configurationLoaded.emit(false);
            this.notificationService.displayError(this.translationService.instant('DEVICE_DETAIL.CONFIGURATION_HAS_BEEN_RELOADED'));
          });

          this.data.device.configurationVersion = status.configurationVersion;
          console.log('last device config version: ', this.data.device.configurationVersion);
        }
      }
    });
  }

  private loadProducts(): Observable<Product[]> {
    const obs = this.apiService.getProducts();
    obs.subscribe((products) => {
      this.allProducts = products;
    });
    return obs;
  }
}


export const thresholdValidator = (): ValidatorFn => {
  return (formGroup: FormGroup): ValidationErrors | null => {
    const lowThresholdControl = formGroup.get('lowThreshold');
    const lowLowThresholdControl = formGroup.get('lowLowThreshold');
    const technicalCapacityControl = formGroup.get('technicalCapacity');

    if (!lowThresholdControl || !lowLowThresholdControl) {
      return null;
    }

    if (lowThresholdControl.value < lowLowThresholdControl.value) {
      return { thresholdsConsistency: true };
    }

    if (technicalCapacityControl === null) {
      return null;
    } else {
      if (parseFloat(technicalCapacityControl.value) < parseFloat(lowThresholdControl.value) || parseFloat(technicalCapacityControl.value) < parseFloat(lowLowThresholdControl.value)) {
        return { thresholdsConsistency: true };
      } else {
        return null;
      }
    }
  };
};
